import React from 'react'

import {groupBy} from 'es-toolkit'
import invariant from 'invariant'
import {Navigate, Route, Routes, useMatch, useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as priceApi from '../../services/api/price'
import * as providerInfoApi from '../../services/api/provider-info'
import {goToCreatePricing, goToPricings} from '../../services/navigation'

import {PriceFormula, PricingTab} from '../../types'

import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Tabs from '../../../../commons/src/components/tabs'
import Tab from '../../../../commons/src/components/tabs/Tab'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import ROUTES from '../../routes'
import PagePartial from '../page-partial'

import Pricings3dPrintingPartial from './pricings-3dPrinting-partial'
import PricingsCncMachiningPartial from './pricings-cncMachining-partial'

const PricingsPage = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  // This will never happen when user is authenticated. Just a safety check for TS.
  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const match = useMatch<'tab', string>({
    path: `${ROUTES.PRICINGS}/:tab`,
  })

  const activeTab: PricingTab = (match?.params.tab as PricingTab) || PricingTab.THREE_D_PRINTING

  const {
    isLoading: isLoadingPrices,
    data: prices = [],
    refresh,
  } = useAsync(() => priceApi.getByProvider(provider.id), [], {
    onError: error => dispatch(fatalError({error})),
    defaultLoading: true,
  })

  const {isLoading: isLoadingProviderInfos, data: providerInfos = []} = useAsync(
    () => providerInfoApi.getByProvider(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
    }
  )

  const isLoading = isLoadingPrices || isLoadingProviderInfos

  const providerInfosByPriceId = React.useMemo(
    () => groupBy(providerInfos, info => info.priceId ?? ''),
    [providerInfos]
  )

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={<Headline size="l" label="Pricing" />}
            actions={
              <Button onClick={() => goToCreatePricing(navigate, {activeTab})}>
                Create new pricing
              </Button>
            }
          >
            <Tabs
              activeTab={activeTab}
              onChange={tabKey =>
                goToPricings(navigate, {activeTab: tabKey as PricingTab, replace: true})
              }
            >
              <Tab label="3D Printing" tabKey={PricingTab.THREE_D_PRINTING} />
              <Tab label="CNC Machining" tabKey={PricingTab.CNC_MACHINING} />
            </Tabs>
          </PageHeader>
        }
      >
        <Container>
          <Routes>
            <Route
              path={PricingTab.THREE_D_PRINTING}
              element={
                <div>
                  <Pricings3dPrintingPartial
                    prices={prices.filter(price => price.formula === PriceFormula.THREE_D_PRINTING)}
                    providerInfosByPriceId={providerInfosByPriceId}
                    isLoading={isLoading}
                    reload={() => refresh()}
                  />
                </div>
              }
            />
            <Route
              path={PricingTab.CNC_MACHINING}
              element={
                <PricingsCncMachiningPartial
                  prices={prices.filter(price => price.formula === PriceFormula.CNC_MACHINING)}
                  providerInfosByPriceId={providerInfosByPriceId}
                  isLoading={isLoading}
                  reload={() => refresh()}
                />
              }
            />
            <Route path="*" element={<Navigate to={ROUTES.PRICINGS} />} />
          </Routes>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default PricingsPage
